import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MdEdit, MdClose, MdMyLocation, MdCameraAlt, MdStar, MdStarBorder } from 'react-icons/md';
import { FiList, FiBarChart2 } from 'react-icons/fi';
import { Tooltip } from 'react-tooltip';
import ModelPicker from '../ModelPicker';

// Shared id for the disabled-feature tooltip. A single <Tooltip> instance
// renders the floating bubble; anchors reference it via data-tooltip-id.
const FEATURE_TIP_ID = 'forecast-feature-tip';

// A feature toggle button (Point Forecast / Skew-T). When disabled (no pin
// chosen yet), it shows a tooltip prompting the user to pick a location.
// The tooltip anchor is the WRAPPING span, not the button — disabled buttons
// don't emit pointer events, so hovering the button alone wouldn't trigger it.
const FeatureButton = ({ icon: Icon, label, active, disabled, hint, onClick, className = '' }) => (
    <span
        className={`block ${className}`}
        data-tooltip-id={disabled ? FEATURE_TIP_ID : undefined}
        data-tooltip-content={disabled ? hint : undefined}
    >
        <button
            type="button"
            disabled={disabled}
            onClick={onClick}
            className={`w-full flex items-center gap-2 px-3 py-2 rounded text-sm
                ${active
                    ? 'bg-blue-600 text-white'
                    : 'text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700'}
                ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        >
            <Icon className="w-4 h-4" />
            {label}
        </button>
    </span>
);

const ForecastSideMenu = ({
    open = true,
    spots = [],
    onLoadSpot,
    onRenameSpot,
    onDeleteSpot,
    activeFeature = null,
    canActivateFeature = false,
    onToggleFeature,
    pin = null,
    onGeolocate,
    geolocating = false,
    geolocationError = null,
    canScreenshot = false,
    onScreenshot,
    canSave = false,
    savedAs = null,
    onSaveCurrent,
    onRenameSaved,
    onRemoveSaved,
}) => {
    const { t } = useTranslation();
    const [editingId, setEditingId] = useState(null);
    const [editValue, setEditValue] = useState('');
    const [saveOpen, setSaveOpen] = useState(false);
    const [draftName, setDraftName] = useState('');

    if (!open) return null;

    const startEdit = (spot) => {
        setEditingId(spot.id);
        setEditValue(spot.name);
    };

    const commitEdit = () => {
        if (editingId) onRenameSpot(editingId, editValue);
        setEditingId(null);
        setEditValue('');
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditValue('');
    };

    const openSave = () => {
        setDraftName(savedAs ? savedAs.name : '');
        setSaveOpen(true);
    };

    const commitSave = () => {
        if (savedAs) {
            onRenameSaved && onRenameSaved(savedAs.id, draftName);
        } else {
            onSaveCurrent && onSaveCurrent(draftName);
        }
        setSaveOpen(false);
    };

    const removeSavedSpot = () => {
        if (savedAs && onRemoveSaved) onRemoveSaved(savedAs.id);
        setSaveOpen(false);
    };

    return (
        <aside className="w-[220px] flex-shrink-0 bg-white dark:bg-slate-800 border-r border-slate-200 dark:border-slate-700 overflow-y-auto">
            <section className="p-3 border-b border-slate-200 dark:border-slate-700">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">
                    {t('Actions')}
                </h3>

                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={onGeolocate}
                        title={geolocationError || t('Use my location')}
                        aria-label={t('Use my location')}
                        className={`w-10 h-10 flex items-center justify-center rounded border border-slate-300 dark:border-slate-600
                            text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer
                            ${geolocating ? 'animate-pulse' : ''}
                            ${geolocationError ? 'border-red-500 text-red-600 dark:text-red-400' : ''}`}
                    >
                        <MdMyLocation className="w-5 h-5" />
                    </button>

                    <button
                        type="button"
                        disabled={!canScreenshot}
                        onClick={onScreenshot}
                        title={t('Screenshot')}
                        aria-label={t('Screenshot')}
                        className={`w-10 h-10 flex items-center justify-center rounded border border-slate-300 dark:border-slate-600
                            text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700
                            ${!canScreenshot ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                        <MdCameraAlt className="w-5 h-5" />
                    </button>

                    <button
                        type="button"
                        disabled={!canSave}
                        onClick={openSave}
                        title={savedAs ? `${t('Saved as')}: ${savedAs.name}` : t('Save spot')}
                        aria-label={t('Save spot')}
                        className={`w-10 h-10 flex items-center justify-center rounded border border-slate-300 dark:border-slate-600
                            text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700
                            ${!canSave ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                        {savedAs ? <MdStar className="w-5 h-5 text-yellow-500" /> : <MdStarBorder className="w-5 h-5" />}
                    </button>
                </div>

                {saveOpen && (
                    <div className="mt-2 p-2 rounded bg-slate-100 dark:bg-slate-700 border border-slate-300 dark:border-slate-600">
                        <label className="block text-xs text-slate-600 dark:text-slate-300 mb-1">
                            {savedAs ? t('Saved as') : t('Name this spot')}
                        </label>
                        <input
                            autoFocus
                            value={draftName}
                            onChange={(e) => setDraftName(e.target.value)}
                            maxLength={60}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') commitSave();
                                if (e.key === 'Escape') setSaveOpen(false);
                            }}
                            className="w-full px-2 py-1 rounded bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 border border-slate-300 dark:border-slate-600 mb-2 text-sm"
                        />
                        <div className="flex gap-2 justify-end">
                            {savedAs && (
                                <button
                                    type="button"
                                    onClick={removeSavedSpot}
                                    className="px-2 py-1 text-xs rounded text-red-600 hover:bg-red-50 dark:hover:bg-slate-600"
                                >
                                    {t('Remove')}
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={() => setSaveOpen(false)}
                                className="px-2 py-1 text-xs rounded text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600"
                            >
                                {t('Close')}
                            </button>
                            <button
                                type="button"
                                onClick={commitSave}
                                className="px-2 py-1 text-xs rounded bg-blue-600 text-white hover:bg-blue-700"
                            >
                                {t('Save')}
                            </button>
                        </div>
                    </div>
                )}
            </section>

            <section className="p-3 border-t border-slate-200 dark:border-slate-700">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2 flex items-center gap-1">
                    <MdMyLocation className="w-3 h-3" />
                    {t('Saved spots')}
                </h3>
                {spots.length === 0 && (
                    <p className="text-xs text-slate-400 dark:text-slate-500 italic">
                        {t('No saved spots yet')}
                    </p>
                )}
                <ul className="space-y-1">
                    {spots.map((spot) => (
                        <li key={spot.id} className="group flex items-center gap-1 text-sm">
                            {editingId === spot.id ? (
                                <input
                                    autoFocus
                                    value={editValue}
                                    onChange={(e) => setEditValue(e.target.value)}
                                    onBlur={commitEdit}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') commitEdit();
                                        if (e.key === 'Escape') cancelEdit();
                                    }}
                                    maxLength={60}
                                    className="flex-1 px-2 py-1 rounded bg-slate-100 dark:bg-slate-700 text-slate-900 dark:text-slate-100 border border-slate-300 dark:border-slate-600"
                                />
                            ) : (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => onLoadSpot(spot)}
                                        className="flex-1 text-left px-2 py-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 truncate"
                                        title={`${spot.lat.toFixed(5)}, ${spot.lng.toFixed(5)}`}
                                    >
                                        {spot.name}
                                    </button>
                                    <button
                                        type="button"
                                        aria-label={t('Rename')}
                                        onClick={() => startEdit(spot)}
                                        className="opacity-0 group-hover:opacity-100 focus:opacity-100 p-1 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                                    >
                                        <MdEdit className="w-4 h-4" />
                                    </button>
                                    <button
                                        type="button"
                                        aria-label={t('Delete')}
                                        onClick={() => onDeleteSpot(spot.id)}
                                        className="opacity-0 group-hover:opacity-100 focus:opacity-100 p-1 text-slate-400 hover:text-red-500"
                                    >
                                        <MdClose className="w-4 h-4" />
                                    </button>
                                </>
                            )}
                        </li>
                    ))}
                </ul>
            </section>

            <section className="p-3 border-t border-slate-200 dark:border-slate-700">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">
                    {t('Model')}
                </h3>
                <div className="flex flex-wrap">
                    <ModelPicker />
                </div>
            </section>

            <section className="p-3 border-t border-slate-200 dark:border-slate-700">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">
                    {t('Features')}
                </h3>
                <FeatureButton
                    icon={FiList}
                    label={t('Point Forecast')}
                    active={activeFeature === 'point'}
                    disabled={!canActivateFeature}
                    hint={t('Click on the map to choose a location')}
                    onClick={() => onToggleFeature('point')}
                />
                <FeatureButton
                    icon={FiBarChart2}
                    label={t('Skew-T')}
                    active={activeFeature === 'skewt'}
                    disabled={!canActivateFeature}
                    hint={t('Click on the map to choose a location')}
                    onClick={() => onToggleFeature('skewt')}
                    className="mt-2"
                />
                <Tooltip
                    id={FEATURE_TIP_ID}
                    place="right"
                    className="!bg-slate-900 !text-white !text-xs !rounded !px-2 !py-1 !opacity-100 z-[1200]"
                />
            </section>
        </aside>
    );
};

export default ForecastSideMenu;
